"use client";

import { type FC } from "react";

import { LocaleItem } from "@/entities/locale";
import type { LocaleMeta } from "@/entities/locale";
import { ItemList, useLocaleSelector } from "@/features/locale-selector";
import { cn } from "@/shared/lib";

interface LocaleSelectorProps {
  /** DDC: locale 메타 데이터 컬렉션 */
  data: readonly LocaleMeta[];
  className?: string;
}

/**
 * Locale 선택 위젯 (DDC Widget)
 *
 * Entity(LocaleItem) + Feature(ItemList + useLocaleSelector) 조합.
 * 글라스모피즘 pill 트리거 + 드롭다운 리스트.
 */
export const LocaleSelector: FC<LocaleSelectorProps> = ({ data, className }) => {
  const { currentLocale, isOpen, toggle, switchLocale, containerRef } = useLocaleSelector();

  const activeMeta = data.find((m) => m.code === currentLocale);

  return (
    <div ref={containerRef} className={cn("relative inline-block", className)}>
      {/* 트리거 버튼 */}
      <button
        type="button"
        onClick={toggle}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Select language"
        className={cn(
          "flex items-center gap-1.5 rounded-full px-3 py-1.5",
          "border border-white/40 bg-white/30 backdrop-blur-md",
          "text-sm font-medium text-gray-700",
          "transition-all hover:bg-white/50 hover:shadow-sm",
          "cursor-pointer"
        )}
      >
        {/* Globe 아이콘 */}
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 21a9 9 0 100-18 9 9 0 000 18z"
          />
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.6 9h16.8M3.6 15h16.8" />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 3a15.3 15.3 0 014 9 15.3 15.3 0 01-4 9 15.3 15.3 0 01-4-9 15.3 15.3 0 014-9z"
          />
        </svg>

        <span>{activeMeta?.shortLabel ?? "KO"}</span>

        {/* Chevron 아이콘 */}
        <svg
          className={cn("h-3 w-3 transition-transform duration-200", isOpen && "rotate-180")}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* 드롭다운 리스트 */}
      {isOpen && (
        <ItemList<LocaleMeta>
          data={data}
          keyExtractor={(item) => item.code}
          onSelect={(item) => switchLocale(item.code)}
          renderItem={(item) => <LocaleItem data={item} isActive={item.code === currentLocale} />}
        />
      )}
    </div>
  );
};
